import {Cache, FetchResult, MutationHookOptions, MutationTuple} from "@apollo/client";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {defineMessage} from "react-intl";
import {Dispatch, MutableRefObject, SetStateAction, useEffect, useMemo, useRef, useState} from "react";
import {Error, Query} from "@/api";
import {clearCache, getCachedData, wrapSaveToCache} from "./formCache";
import ErrorHelper from "./ErrorHelper";
import {DeepPartial, deepUpdateObject} from "@/tools";
import isEqual from "lodash/isEqual";

export type MutationHookFunction<R, P> = (baseOptions?: MutationHookOptions<R, P>) => MutationTuple<R, P>;
export type MutationFunction<R, P> = MutationTuple<R, P>[0];

export type FormNotification<T extends Form<any, any>> = (form: T) => any;

type FormSubmitEvictCache = {
    fieldName: keyof Query;
    args?: Record<string, any>;
};

type FormSubmitOptions = {
    evictCaches?: FormSubmitEvictCache[];
    /// If true, the initial data will be updated after a successful submission.
    updateInitialData?: boolean;
};

export class Form<
    Mutation,
    MutationVariables extends object,
    Values extends object = MutationVariables,
    Initial extends Values | (() => Values) = Values,
> {
    values: Values;
    errors: Error[];
    private _errorHelper: MutableRefObject<ErrorHelper>;

    protected setValues: (values: Values) => void;
    protected setErrors: (errors: Error[]) => void;
    protected mutate: ReturnType<MutationHookFunction<Mutation, MutationVariables>>[0];
    protected dispatch: any;
    protected readonly dataKey: keyof Mutation;

    protected initialData: Values;
    protected setInitialData: (values: Values) => void;

    protected initialDataGetter: () => Values | undefined;

    protected successNotification: (form: Form<Mutation, MutationVariables, Values, Initial>) => object;
    protected errorNotification: (form: Form<Mutation, MutationVariables, Values, Initial>) => object;
    protected _result: MutableRefObject<FetchResult<Mutation>>;
    protected setResult: (data: FetchResult<Mutation> | null) => void;

    submitting: boolean;
    private setSubmitting: Dispatch<SetStateAction<boolean>>;

    cacheKeyPostfix: string;
    setCacheKeyPostfix: Dispatch<SetStateAction<string>>;

    constructor(dataKey: keyof Mutation) {
        this.dataKey = dataKey;
    }

    hooks(
        mutation: MutationHookFunction<Mutation, MutationVariables>,
        initialData: Initial,
        successNotification?: FormNotification<Form<Mutation, MutationVariables, Values, Initial>>,
        errorNotification?: FormNotification<Form<Mutation, MutationVariables, Values, Initial>>,
        cacheKeyPostfix?: string,
    ) {
        const defaultSuccessNotification = () =>
            showNotification(defineMessage({id: "save_success", defaultMessage: "Data successfully saved"}), "success");
        const defaultErrorNotification = () =>
            showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error");

        [this.initialData, this.setInitialData] = useState(
            typeof initialData === "function" ? initialData() : initialData,
        );

        if (typeof initialData === "function") {
            this.initialDataGetter = initialData as () => Values;
        }

        this.successNotification = successNotification ?? defaultSuccessNotification;
        this.errorNotification = errorNotification ?? defaultErrorNotification;

        [this.mutate] = mutation();
        [this.values, this.setValues] = useState(structuredClone(this.initialData));
        [this.errors, this.setErrors] = useState([]);
        [this.submitting, this.setSubmitting] = useState(false);
        this._result = useRef(null);
        this.dispatch = useDispatch();
        this._errorHelper = useRef<ErrorHelper>(null);

        [this.cacheKeyPostfix, this.setCacheKeyPostfix] = useState(cacheKeyPostfix);

        useEffect(() => {
            this.setValues(getCachedData(cacheKeyPostfix) || this.copyInitialData());
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [cacheKeyPostfix, this.initialData]);

        wrapSaveToCache(this, this.updateValues.name, cacheKeyPostfix);

        useEffect(() => {
            if (this.hasChanged === false) {
                if (this.errors.length > 0) this.updateErrors([]);
                clearCache(cacheKeyPostfix);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [cacheKeyPostfix, this.hasChanged]);

        useEffect(() => {
            this.reset();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [this.initialData]);
    }

    clearCache() {
        clearCache(this.cacheKeyPostfix);
    }

    protected createErrorHelper(...args: ConstructorParameters<typeof ErrorHelper>): ErrorHelper {
        return new ErrorHelper(...args);
    }

    get errorHelper(): ErrorHelper {
        return this._errorHelper.current ?? this.createErrorHelper([]);
    }

    protected copyInitialData(): Values {
        return structuredClone(this.initialData);
    }

    get hasChanged(): boolean {
        return isEqual(this.values, this.initialData) === false;
    }

    get result(): FetchResult<Mutation> | null {
        return this._result.current;
    }

    reset() {
        this.updateValues(this.copyInitialData());
        this.clearErrors();
    }

    updateValues(data: DeepPartial<Values>): Values {
        const newData = deepUpdateObject(structuredClone(this.values), data);
        this.setValues(newData);
        return newData;
    }

    addError(error: Error) {
        for (const currentError of this.errors) {
            const location = error.location.join(".");
            const currentLocation = currentError.location.join(".");
            if (error.type === currentError.type && location === currentLocation) return;
        }

        this.updateErrors([...this.errors, error]);
    }

    protected updateErrors(errors: Error[]) {
        this._errorHelper.current = this.createErrorHelper(errors);
        this.setErrors(errors);
    }

    async submit(options?: FormSubmitOptions): Promise<boolean> {
        this.setSubmitting(true);

        const mutateOptions: MutationHookOptions<Mutation, MutationVariables> = {
            variables: this.filteredVariables,
        };

        if (options?.evictCaches) {
            mutateOptions.update = (cache) => {
                for (const evictCache of options.evictCaches) {
                    const evictOptions: Cache.EvictOptions = {
                        id: "ROOT_QUERY",
                        fieldName: evictCache.fieldName,
                        args: evictCache.args,
                        broadcast: false,
                    };

                    cache.evict(evictOptions);
                }
            };
        }

        this._result.current = await this.mutate(mutateOptions);

        const response = this.result.data[this.dataKey] as any;
        if (response["errors"]) {
            this.updateErrors(response["errors"]);

            if (response["errors"].filter((error: Error) => !error.type.startsWith("value_error.")).length) {
                this.dispatch(this.errorNotification(this));
            }

            this.setSubmitting(false);
            return false;
        }

        this.dispatch(this.successNotification(this));
        if (options?.updateInitialData !== false) {
            this.setInitialData(structuredClone(this.values));
        }
        this.clearErrors();
        this.setSubmitting(false);
        return true;
    }

    protected get filteredVariables(): MutationVariables {
        return structuredClone(this.values as any);
    }

    newInitialData(newInitialData?: Initial) {
        if (!newInitialData) {
            if (this.initialDataGetter !== undefined) {
                this.updateValues(structuredClone(this.initialDataGetter()));
            }
            return;
        }

        this.setInitialData(typeof newInitialData === "function" ? newInitialData() : newInitialData);
    }

    clearErrors() {
        this.updateErrors([]);
    }
}

/**
 * Create a form to handle the mutation and its data.
 */
export function useForm<
    Mutation,
    MutationVariables extends object,
    Values extends object = MutationVariables,
    Initial extends Values | (() => Values) = Values,
>(
    mutation: MutationHookFunction<Mutation, MutationVariables>,
    dataKey: keyof Mutation,
    initialData: Initial,
    successNotification?: FormNotification<Form<Mutation, MutationVariables, Values, Initial>>,
    errorNotification?: FormNotification<Form<Mutation, MutationVariables, Values, Initial>>,
    cacheKeyPostfix?: string,
) {
    const form = useMemo(() => new Form<Mutation, MutationVariables, Values, Initial>(dataKey), [dataKey]);
    form.hooks(mutation, initialData, successNotification, errorNotification, cacheKeyPostfix);
    return form;
}
